import config from '@SonarCX/config'
import io from 'socket.io-client'

import { createTypingEvent, createMessage, createNote } from './emc2p'
import { chunkArray, uniqueArray } from '@SonarCX/utilities'
import { getStoredAccount } from '@SonarCX/providers/AccountProvider'
import { getAccessToken } from '@SonarCX/index'

// import * as api from '../api'

const READ_TIMEOUT = 2000
const PRESENCE_TIMEOUT = 2000

// For caching information on current users for completing challenges etc.
class AgentService {
  // The socket handler
  config = null
  // Session key
  key = null
  // Socket var
  socket = null
  // Current user
  user = null
  userToken = null
  // Handlers
  receiveHandler = null
  // Queued read messages
  sendQueue = []
  sendTimer = null
  // Queue presence requests
  presenceQueue = new Set()
  presenceTimer = null
  presenceDone = new Set()
  connected = false

  constructor (config) {
    this.config = config
  }

  setSessionKey = key => {
    this.key = key
  }

  setUser = user => {
    this.user = user
  }

  setUserToken = token => {
    this.userToken = token
  }

  currentUser = () => {
    return this.user
  }

  // Sets and stores current user and token
  setUserSession = (user, token) => {
    this.setUser(user)
    this.setUserToken(token)
    localStorage.setItem('session_token', token)
  }

  // Returns part of the session
  getUserSession = key => {
    if (key === 'user') {
      return this.user
    } else if (key === 'token') {
      return this.userToken
    } else {
      return [this.user, this.userToken]
    }
  }

  // Sends a message
  sendSessionMessage = ({
    id,
    message,
    title = undefined,
    isUser = false,
    channels = [],
    media = [],
    correlationId,
    action_type,
    group
  }) => {
    if (!this.user) {
      // Not signed in!
      return
    }

    if (typeof message !== 'string') {
      return
    }

    if (message || media.length) {
      // const fixedMessage = message?.replace(
      //   /#\((\w+)\)/g,
      //   (_, tag) => `#${tag}`
      // )
      // const [body, tags] = parseTags(fixedMessage)

      // Post tags before sending
      // Promise.all(
      //   tags.map(async tag => {
      //     api.postTag(tag).catch(err => {
      //       console.error('Post error for tag in agent message', err)
      //     })
      //   })
      // ).then(() => {
      const payload = createMessage({
        body: message,
        id,
        isUser: isUser && !group,
        media,
        tags: [],
        sideChannels: channels,
        title,
        correlationId,
        action_type
      })

      if (this.socket) {
        this.socket.emit('message', payload)
      }
      const wasUser = isUser && !group
      this.updateRead(wasUser ? id : null, wasUser ? null : id)
      // })
    }
  }

  // Sends a text message
  sendTextMessage = async ({
    id,
    message,
    isUser = false,
    media,
    correlationId,
    group
  }) => {
    const { content, number } = message

    this.sendSessionMessage({
      id,
      message: content,
      isUser,
      channels: Array.isArray(number) ? number : [number],
      media,
      correlationId,
      action_type: 'sms',
      group
    })
  }

  // Sends an email
  sendEmail = async ({
    id,
    message,
    isUser = false,
    media,
    correlationId,
    group
  }) => {
    const { content, title, email } = message

    this.sendSessionMessage({
      id,
      message: content,
      isUser,
      channels: Array.isArray(email) ? email : [email],
      media,
      title,
      correlationId,
      action_type: 'email',
      group
    })
  }

  // Sends a private note, only works in sessions
  sendSessionNote = ({ id, message, media = [] }) => {
    if (!this.user) {
      // Not signed in!
      return
    }

    // const fixedMessage = message.replace(/#\((\w+)\)/g, (_, tag) => `#${tag}`)
    // const [body, tags] = parseTags(fixedMessage)

    // Post tags before sending
    // Promise.all(
    //   tags.map(async tag => {
    //     api.postTag(tag).catch(err => {
    //       console.error('Post error for tag in agent note', err)
    //     })
    //   })
    // ).then(() => {
    const payload = createNote({ body: message, id, media, tags: [] })

    if (this.socket) {
      this.socket.emit('message', payload)
    }
    // })
  }

  // Sends the typing event
  sendSessionTypingEvent = (key, isUser = false, allAgents) => {
    if (!this.user) {
      // Not signed in!
      return
    }

    // check if group chat, p2p or client
    const p2p = allAgents[key]
    if (!p2p) {
      isUser = false
    }
    const payload = createTypingEvent(key, isUser)

    new Promise(resolve => {
      if (this.socket) {
        resolve(this.socket.emit('event', payload))
      }
    })
  }

  // Receive handler for socket events
  onReceive = event => {
    if (this.receiveHandler) {
      this.receiveHandler(event)
    } else {
      console.error('Handler not yet established!!!')
    }
  }

  registerHandlers = onReceive => {
    this.receiveHandler = onReceive
  }

  deregisterHandlers = () => {
    this.receiveHandler = null

    if (this.socket) {
      this.socket.off('error')
      this.socket.off('message')
      this.socket.off('event')
    }
  }

  // Connects to the remote session and sets up some event handlers for the socket
  establishSession = async ({ token, onConnect, onError, onDisconnect }) => {
    const account = getStoredAccount()
    const tkn = await getAccessToken()

    this.connected = false

    // Connect as agent
    this.socket = io.connect(this.config.host, {
      path: this.config.path,
      query: {
        token: `Bearer ${tkn}`,
        account_id: account
      },
      transports: ['websocket', 'polling']
    })

    // Register error handler.
    this.socket.on('error', error => {
      console.error('<< [ERROR] ', error)
      onError(error)
    })

    // Register disconnection handler
    this.socket.on('disconnect', event => {
      this.connected = false
      onDisconnect(event)
    })

    // This is the standard event workflow.
    this.socket.on('event', this.onReceive)

    // Route subscription updates to the current handler
    this.socket.on('subscriptions', this.onReceive)

    // Send notifications to socket
    this.socket.on('notify', this.onReceive)

    // System events, just merge for now
    this.socket.on('system', sys => {
      if (sys.action === 'hello' && !this.connected) {
        this.connected = true
        onConnect()
      } else {
        this.onReceive(sys)
      }
    })

    // Task events
    this.socket.on('task', this.onReceive)
    this.socket.on('task.comment', this.onReceive)

    // User events
    this.socket.on('user', this.onReceive)

    // Session events
    this.socket.on('session', ses => {
      this.onReceive({ ...ses, action: ses.action ?? 'session' })
    })

    this.socket.on('connect', () => {
      this.socket.sendBuffer = []
    })
  }

  disconnect = () => {
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
  }

  // Joins a session and calls the callback on success
  joinSession = (session, onConnect) => {
    return new Promise((resolve, _reject) => {
      if (!this.socket) {
        resolve()
        return
      }

      this.socket.emit(
        'session',
        {
          action: 'join',
          sessionId: session,
          userId: null,
          content: null
        },
        () => {
          onConnect && onConnect()
          resolve()
        }
      )
    })
  }

  // Subscribes this socket to changes in the provided lists
  subscribeToInboxes = inboxes => {
    if (this.socket && inboxes?.length > 0) {
      this.socket.emit(
        'session',
        {
          action: 'subscribe_inbox',
          content: inboxes,
          userId: null,
          sessionId: null
        },
        resp => {}
      )

      return true
    }

    return false
  }

  // Fetches presence from socket
  fetchPresence = (agents, handler) => {
    // Add ids to queue
    this.presenceQueue = new Set([
      ...this.presenceQueue,
      ...agents.filter(Boolean)
    ])

    // Clear old timeout and create new
    clearTimeout(this.presenceTimer)

    this.presenceTimer = setTimeout(() => {
      // Get unchecked ids
      const ids = [...this.presenceQueue].filter(
        id => !this.presenceDone.has(id)
      )

      // Clear the queue
      this.presenceQueue.clear()

      // Separate into batches of 25
      const chunks = chunkArray(ids, 25)

      // Get presence
      chunks.forEach(chunk => {
        if (chunk.length > 0) {
          if (this.socket) {
            this.socket.emit(
              'presence',
              {
                action: 'inquiry',
                content: chunk
              },
              (...args) => {
                // Update done list to prevent duplicates
                this.presenceDone = new Set([...this.presenceDone, ...chunk])
                handler(...args)
              }
            )
          }
        }
      })
    }, PRESENCE_TIMEOUT)
  }

  // Update presence using socket
  updatePresence = () => {
    if (this.socket) {
      this.socket.emit('presence', { action: 'ping' })
    }
  }

  // Sends a de-duped me_read event to a peer chat or session
  updateRead = (userId, sessionId) => {
    // Add request to queue
    this.sendQueue.push({
      userId: userId ?? null,
      sessionId: sessionId ?? null
    })

    // Clear old timeout and create new
    clearTimeout(this.sendTimer)

    this.sendTimer = setTimeout(() => {
      // Get a unique set of requests
      const asStrings = this.sendQueue.map(JSON.stringify)
      const uniqueQueue = uniqueArray(asStrings).map(JSON.parse)

      // Send queued requests
      uniqueQueue.forEach(item => {
        const { userId, sessionId } = item

        // Send the me_read event
        if (this.socket) {
          this.socket.emit('event', {
            action: 'me_read',
            action_type: null,
            userId,
            sessionId,
            content: { stamp: new Date().toISOString() }
          })
        }
      })

      // Clear the queue
      this.sendQueue = []
    }, READ_TIMEOUT)
  }
}

// Our singleton object
const agentConnection = new AgentService({
  host: config.chatHost,
  path: config.chatPath
})

export default agentConnection
